const PictProvider = require('pict-provider');

/** @typedef {import('../HeadlightConfigurableApplication.js').Pict} Pict */

/** The postMessage channel both sides filter on. The parent copy lives in web/headlight-platform-configuration/source/documents/DocumentFrameSwitcher-Provider.js. */
const CHANNEL = 'headlight-document-frame';

/** The query parameter a parent puts on the form URL to adopt the page as one of its document frames. */
const FRAME_PARAMETER = 'HeadlightFrame';

/** State changes arrive per keystroke; the parent only needs to hear about them a few times a second. */
const NOTIFY_DEBOUNCE_MS = 250;

/**
 * @typedef {Object} DocumentFrameState
 * @property {boolean} Ready - Whether the form has finished loading and drawing.
 * @property {number} IDDocument
 * @property {String} Status - The document's approval status.
 * @property {boolean} Locked
 * @property {boolean} CanEdit
 * @property {boolean} HasUnsavedChanges - The user changed the document since it was loaded or last saved.
 * @property {boolean} Saving
 * @property {boolean} ApprovalBusy - An approval action is running.
 * @property {boolean} ReloadPending - An approval action landed and the page is about to reload itself.
 */

/**
 * Lets a same-origin parent page host this document form in an iframe and work with it: the parent learns
 * when the form is ready, whether it holds unsaved changes and what its approval state is, and can ask it
 * to save. The form itself is untouched; saving goes through DocumentProvider exactly as the Save button does.
 *
 * Inert unless the page is framed and its URL carries `HeadlightFrame=<id>`, so the standalone form, the PDF
 * render and the iOS offline template never see it.
 *
 * Messages are `{ channel, frameId, type, ... }`. Parent to frame: `getState`, `save` (each with a `requestId`).
 * Frame to parent: `ready` (with the `[FormLoad]` timing summary), `state`, and the `requestId`-tagged replies
 * `state` and `saveResult`.
 */
class DocumentFrameProvider extends PictProvider
{
	/**
	 * @param {any} pFable - The Fable instance.
	 * @param {Object} [pOptions] - The provider options.
	 * @param {String} [pServiceHash] - The fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		/** @type {any} */
		this.log;

		/** @type {?String} */
		this.FrameID = DocumentFrameProvider.readFrameID(typeof window === 'undefined' ? null : window);
		/** @type {boolean} */
		this.Enabled = !!this.FrameID;
		/** @type {boolean} */
		this.Ready = false;
		/** @type {?String} - The last state posted, so an unchanged state is not re-sent. */
		this._LastPostedState = null;
		/** @type {any} */
		this._NotifyTimer = null;

		if (this.Enabled)
		{
			this._onMessage = this._onMessage.bind(this);
			window.addEventListener('message', this._onMessage);
		}
	}

	/**
	 * The frame id the parent assigned, or null when this page is not hosted as a document frame.
	 *
	 * @param {any} pWindow - The page's window.
	 * @return {?String}
	 */
	static readFrameID(pWindow)
	{
		if (!pWindow || !pWindow.parent || (pWindow.parent === pWindow) || !pWindow.location)
		{
			return null;
		}
		try
		{
			const tmpFrameID = new URLSearchParams(pWindow.location.search).get(FRAME_PARAMETER);
			return tmpFrameID || null;
		}
		catch (pError)
		{
			return null;
		}
	}

	/**
	 * @return {DocumentFrameState}
	 */
	getState()
	{
		const tmpReportData = this.pict.AppData?.DocumentData?.ReportData || { };
		const tmpDocumentProvider = this.pict.providers?.DocumentProvider;
		const tmpAutoSave = this.pict.providers?.AutoSave;
		let tmpHasUnsavedChanges = false;
		try
		{
			tmpHasUnsavedChanges = !!(tmpAutoSave && (typeof tmpAutoSave.hasUnsavedUserChanges === 'function') && tmpAutoSave.hasUnsavedUserChanges());
		}
		catch (pError)
		{
			this.log.warn(`DocumentFrame: could not tell whether the document has unsaved changes: ${pError?.message || pError}`);
		}
		return {
			Ready: this.Ready,
			IDDocument: Number(tmpReportData.IDDocument) || 0,
			Status: tmpReportData.Status || '',
			Locked: !!tmpReportData.Locked,
			CanEdit: !!tmpReportData.CanEdit,
			HasUnsavedChanges: tmpHasUnsavedChanges,
			Saving: !!tmpDocumentProvider?.IsSaving,
			ApprovalBusy: !!tmpDocumentProvider?.isTakingAction,
			ReloadPending: !!tmpDocumentProvider?._ApprovalReloadPending,
		};
	}

	/**
	 * The form has loaded and drawn; tell the parent, with the load timing. Once per page load.
	 *
	 * @param {any} [pTiming] - The `[FormLoad]` summary.
	 * @return {void}
	 */
	announceReady(pTiming)
	{
		if (!this.Enabled || this.Ready)
		{
			return;
		}
		this.Ready = true;
		const tmpState = this.getState();
		this._LastPostedState = JSON.stringify(tmpState);
		this._post({ type: 'ready', state: tmpState, timing: pTiming || null });
	}

	/**
	 * Something the parent shows may have changed (an edit, a save, an approval action). Debounced.
	 *
	 * @param {boolean} [pImmediately] - Post now, e.g. when the page is about to reload.
	 * @return {void}
	 */
	notifyChanged(pImmediately)
	{
		if (!this.Enabled)
		{
			return;
		}
		if (this._NotifyTimer)
		{
			clearTimeout(this._NotifyTimer);
			this._NotifyTimer = null;
		}
		if (pImmediately)
		{
			this._postStateIfChanged();
			return;
		}
		this._NotifyTimer = setTimeout(() =>
		{
			this._NotifyTimer = null;
			this._postStateIfChanged();
		}, NOTIFY_DEBOUNCE_MS);
	}

	/** Post the current state unless it is what the parent already has. */
	_postStateIfChanged()
	{
		const tmpState = this.getState();
		const tmpSerialized = JSON.stringify(tmpState);
		if (tmpSerialized === this._LastPostedState)
		{
			return;
		}
		this._LastPostedState = tmpSerialized;
		this._post({ type: 'state', state: tmpState });
	}

	/**
	 * @param {MessageEvent} pEvent
	 * @return {void}
	 */
	_onMessage(pEvent)
	{
		const tmpData = pEvent?.data;
		if (!tmpData || (tmpData.channel !== CHANNEL) || (tmpData.frameId !== this.FrameID))
		{
			return;
		}
		if ((pEvent.source !== window.parent) || (pEvent.origin !== window.location.origin))
		{
			return;
		}
		switch (tmpData.type)
		{
		case 'getState':
				this._post({ type: 'state', requestId: tmpData.requestId, state: this.getState() });
			return;
		case 'save':
				this._save(tmpData.requestId);
			return;
		default:
			return;
		}
	}

	/**
	 * @param {String} pRequestID
	 * @return {Promise<void>}
	 */
	async _save(pRequestID)
	{
		const tmpDocumentProvider = this.pict.providers?.DocumentProvider;
		let tmpError = null;
		if (!tmpDocumentProvider || (typeof tmpDocumentProvider.handleSaveAsync !== 'function'))
		{
			tmpError = 'This form cannot be saved.';
		}
		else
		{
			try
			{
				const tmpSaveError = await tmpDocumentProvider.handleSaveAsync(false);
				if (tmpSaveError)
				{
					tmpError = tmpSaveError.message || String(tmpSaveError);
				}
			}
			catch (pError)
			{
				tmpError = pError?.message || String(pError);
			}
		}
		const tmpState = this.getState();
		this._LastPostedState = JSON.stringify(tmpState);
		this._post({ type: 'saveResult', requestId: pRequestID, error: tmpError, state: tmpState });
	}

	/**
	 * @param {Record<String, any>} pMessage
	 * @return {void}
	 */
	_post(pMessage)
	{
		try
		{
			window.parent.postMessage(Object.assign({ channel: CHANNEL, frameId: this.FrameID }, pMessage), window.location.origin);
		}
		catch (pError)
		{
			this.log.warn(`DocumentFrame: could not reach the hosting page: ${pError?.message || pError}`);
		}
	}
}

module.exports = DocumentFrameProvider;
module.exports.CHANNEL = CHANNEL;
module.exports.FRAME_PARAMETER = FRAME_PARAMETER;
